import { type MutableRefObject, useEffect, useRef } from "react";
import * as echarts from "echarts/core";
import { BarChart, LineChart } from "echarts/charts";
import {
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  TooltipComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import type { EChartsCoreOption, ECharts } from "echarts/core";

// Register only the pieces we use so the bundle stays small (echarts is large if
// imported whole). Add more charts/components here as other pages adopt them.
echarts.use([
  LineChart,
  BarChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
  // markArea is its own component: without it the shaded bands (coverage gaps, the
  // time-of-day tint) are silently dropped from the option
  MarkAreaComponent,
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  CanvasRenderer,
]);

// Thin React wrapper around an ECharts instance: inits once, re-applies `option`
// on change, resizes with its container, and disposes on unmount.
export function EChart({
  option,
  height = 150,
  className,
  instanceRef,
}: {
  option: EChartsCoreOption;
  height?: number;
  className?: string;
  // Escape hatch for the imperative half of the API (dispatchAction, on/off): a caller
  // that owns a zoom-reset control needs the instance, and everything else stays declarative.
  instanceRef?: MutableRefObject<ECharts | null>;
}) {
  const elRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ECharts | null>(null);

  useEffect(() => {
    if (!elRef.current) return;
    const chart = echarts.init(elRef.current, undefined, { renderer: "canvas" });
    chartRef.current = chart;
    if (instanceRef) instanceRef.current = chart;
    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(elRef.current);
    return () => {
      ro.disconnect();
      chart.dispose();
      chartRef.current = null;
      if (instanceRef) instanceRef.current = null;
    };
  }, [instanceRef]);

  // `notMerge: true` so swapping series/axes never leaves stale state behind.
  useEffect(() => {
    chartRef.current?.setOption(option, true);
  }, [option]);

  return <div ref={elRef} className={className} style={{ width: "100%", height }} />;
}

// Resolve a CSS custom property off the document root so charts match the active
// (light/dark) theme. Falls back when the var is missing.
//
// Colour tokens are declared with light-dark(), which getPropertyValue() returns
// as the unresolved literal `light-dark(#a, #b)` (likewise color-mix()). ECharts
// cannot parse that, so for those we resolve to a concrete rgb() via a probe
// element that inherits the active color-scheme. Non-colour vars (fonts) and
// already-concrete values pass through unchanged.
export function cssVar(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!raw) return fallback;
  if (raw.includes("light-dark(") || raw.includes("color-mix(")) {
    const probe = document.createElement("span");
    probe.style.color = `var(${name})`;
    probe.style.display = "none";
    document.body.appendChild(probe);
    const resolved = getComputedStyle(probe).color;
    probe.remove();
    return resolved || fallback;
  }
  return raw;
}
